import { useLingui } from '@lingui/react/macro';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';

import { BASE_PATH } from '@/constants/config';
import {
  type ResearcherSignupRequest,
  type SignupSuccess,
  signupSuccessSchema,
  AuthError,
} from '@/routes/-auth/contract';
import { utmSearchSchema } from '@/schemas/utm';
import { detectTimezone } from '@/utils/detect-timezone';
import { ERROR_CODES, readErrorCode, readErrorMessage } from '@common/utils';

export type UTM = NonNullable<ResearcherSignupRequest['initialUtm']>;

/** Exported for tests: the payload is untrusted, so its parsing is the risky part. */
export function toSignupError(payload: unknown): AuthError {
  const code = readErrorCode(payload);
  const message = readErrorMessage(payload);

  switch (code) {
    case ERROR_CODES.CAPTCHA_CHALLENGE_REQUIRED:
    case ERROR_CODES.ACCOUNT_EXISTS:
      return new AuthError(code, message);
    case ERROR_CODES.SSO_REDIRECT_REQUIRED: {
      // The server is contractually required to send a redirect with this
      // code. Without one there is nowhere to send the user, so this is an
      // ordinary failure rather than a code the page will act on.
      const redirect =
        typeof payload === 'object' &&
        payload !== null &&
        'redirect' in payload &&
        typeof payload.redirect === 'string'
          ? payload.redirect
          : undefined;
      return redirect
        ? new AuthError(code, message, redirect)
        : new AuthError(
            ERROR_CODES.SIGNUP_FAILED,
            message ?? 'SSO redirect URL missing',
          );
    }
    default:
      return new AuthError(
        ERROR_CODES.SIGNUP_FAILED,
        message ?? 'Signup failed',
      );
  }
}

export const researcherSignupSearchSchema = utmSearchSchema.extend({
  referralCode: z.string().optional(),
  r: z.string().optional(),
  'plan-type': z.string().optional(),
  'participant-type': z.string().optional(),
});

export const useSignupMutation = () => {
  const { t } = useLingui();
  const schema = z
    .object({
      firstName: z
        .string()
        .trim()
        .min(1, t`First name is required`),
      lastName: z
        .string()
        .trim()
        .min(1, t`Last name is required`),
      email: z
        .email(t`Please enter a valid email address`)
        .trim()
        .min(1, t`Work email is required`),
      password: z.string().min(8, t`Password must be at least 8 characters`),
      confirmPassword: z.string().min(1, t`Please confirm your password`),
      termsAccepted: z.boolean().refine((value) => value === true, {
        message: t`You must accept the Terms & Conditions and Privacy Policy`,
      }),
    })
    .refine((value) => value.password === value.confirmPassword, {
      message: t`Passwords must match`,
      path: ['confirmPassword'],
    });

  type FormFields = z.infer<typeof schema>;

  type SignupVariables = {
    fields: FormFields;
    captchaToken: string;
    initialUtm: Maybe<UTM>;
    referralCode: Maybe<string>;
    captchaV2Token?: string;
    /**
     * The banner decision this browser recorded. Resolved by the caller, which
     * is where the consent context lives.
     */
    cookieSettings?: ResearcherSignupRequest['cookieSettings'];
  };

  const mutation = useMutation<SignupSuccess, AuthError, SignupVariables>({
    mutationFn: async ({
      fields,
      captchaToken,
      initialUtm,
      referralCode,
      captchaV2Token,
      cookieSettings,
    }) => {
      const timezone = detectTimezone();
      const requestBody: ResearcherSignupRequest = {
        firstName: fields.firstName.trim(),
        lastName: fields.lastName.trim(),
        email: fields.email.trim(),
        password: fields.password,
        termsAccepted: fields.termsAccepted,
        captchaToken,
        ...(initialUtm && { initialUtm }),
        ...(referralCode && { referralCode }),
        ...(captchaV2Token && { captchaV2Token }),
        ...(timezone && { timezone }),
        ...(cookieSettings && { cookieSettings }),
      };

      const response = await fetch(`${BASE_PATH}/api/auth/researcher-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
        credentials: 'include',
      });

      if (!response.ok) {
        throw toSignupError(await response.json());
      }

      // Validate at the boundary so `data` is correctly typed downstream and
      // a malformed success response is treated as a hard failure.
      const parsed = signupSuccessSchema.safeParse(await response.json());
      if (!parsed.success) {
        throw new AuthError(
          ERROR_CODES.SIGNUP_FAILED,
          'Invalid response from registration service',
        );
      }
      return parsed.data;
    },
  });

  return { mutation, schema };
};
